import React, { useEffect, useRef, useState } from "react";
import { searchClients } from "../services/daysmartService";
import { updateCommunicationChainClient, formatPhoneNumber } from "../services/communicationCenterService";
import { isWeakNameLink, matchReasonLabel, senderLabel } from "../utils/clientLink";

const buttonClass = "px-2.5 py-1 rounded-md text-xs font-medium disabled:opacity-50";
const clientPhone = (client) => client.cell_phone || client.home_phone || client.alternate_phone || "";

const ClientLinkNotice = ({ chain, userId, onChanged }) => {
    const [busy, setBusy] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [results, setResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [error, setError] = useState(null);
    const mountedRef = useRef(false);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);

    const weak = isWeakNameLink(chain);
    const suggestion = chain.suggested_client || null;
    const candidates = (chain.client_match_candidates || []).filter(
        (candidate) => !suggestion || candidate.client_id !== suggestion.client_id
    );
    const showSearch = weak || !chain.client_id;
    if (!weak && !suggestion && candidates.length === 0 && chain.client_id) {
        return null;
    }

    const run = async (work) => {
        setBusy(true);
        setError(null);
        try {
            await work();
            if (!mountedRef.current) return;
            setResults([]);
            setSearchTerm("");
            if (onChanged) await onChanged();
        } catch (err) {
            if (mountedRef.current) setError(err.message || "Could not update the client link");
        } finally {
            if (mountedRef.current) setBusy(false);
        }
    };

    const linkClient = (client, { keepPhone = false } = {}) => run(() => {
        const pets = client.pet_names
            || (client.pets || []).map((pet) => pet.name || pet.pet_name).filter(Boolean);
        const payload = {
            action: "link",
            clientId: client.client_id,
            clientName: client.client_name || `${client.first_name || ""} ${client.last_name || ""}`.trim(),
            petNames: pets,
            userId,
        };
        if (!keepPhone) {
            payload.clientPhone = clientPhone(client);
        }
        return updateCommunicationChainClient(chain.chain_id, payload);
    });

    const sender = senderLabel(chain);
    const senderEmail = chain.sender_email || chain.client_email;

    return (
        <div
            className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-950"
            onClick={(event) => event.stopPropagation()}
        >
            {weak && (
                <div className="flex flex-wrap items-center gap-2">
                    <p className="flex-1 min-w-[16rem]">
                        Linked by last name only. Sender is {sender || "unknown"}
                        {senderEmail ? ` (${senderEmail})` : ""}. DaySmart client is {chain.client_name}.
                        Confirm this client before calling or texting.
                    </p>
                    <button
                        type="button"
                        disabled={busy}
                        onClick={() => linkClient({
                            client_id: chain.client_id,
                            client_name: chain.client_name,
                            pet_names: chain.pet_names || [],
                        }, { keepPhone: true })}
                        className={`${buttonClass} bg-amber-700 text-white hover:bg-amber-800`}
                    >
                        Confirm client
                    </button>
                    <button
                        type="button"
                        disabled={busy}
                        onClick={() => run(() => updateCommunicationChainClient(chain.chain_id, {
                            action: "unlink",
                            userId,
                        }))}
                        className={`${buttonClass} bg-white text-amber-900 border border-amber-300 hover:bg-amber-100`}
                    >
                        Unlink
                    </button>
                </div>
            )}

            {!chain.client_id && sender && (
                <p className="text-xs text-amber-800 mb-1">
                    From {sender}{senderEmail ? ` (${senderEmail})` : ""}. Not linked to a DaySmart client.
                </p>
            )}

            {suggestion && (
                <div className="flex flex-wrap items-center gap-2 mt-1">
                    <p className="flex-1 min-w-[16rem]">
                        Possible match: {suggestion.client_name}
                        {clientPhone(suggestion) ? ` · ${formatPhoneNumber(clientPhone(suggestion))}` : ""}
                        {matchReasonLabel(suggestion.reason || chain.client_match_reason)
                            ? ` · ${matchReasonLabel(suggestion.reason || chain.client_match_reason)}`
                            : ""}
                    </p>
                    <button
                        type="button"
                        disabled={busy}
                        onClick={() => linkClient(suggestion)}
                        className={`${buttonClass} bg-amber-700 text-white hover:bg-amber-800`}
                    >
                        Use this client
                    </button>
                    <button
                        type="button"
                        disabled={busy}
                        onClick={() => run(() => updateCommunicationChainClient(chain.chain_id, {
                            action: "dismiss_suggestion",
                            userId,
                        }))}
                        className={`${buttonClass} bg-white text-amber-900 border border-amber-300 hover:bg-amber-100`}
                    >
                        Not this client
                    </button>
                </div>
            )}

            {candidates.length > 0 && (
                <div className="mt-2 space-y-1">
                    {matchReasonLabel(chain.client_match_reason) && (
                        <p className="text-xs font-medium text-amber-800">
                            {matchReasonLabel(chain.client_match_reason)}
                        </p>
                    )}
                    {candidates.map((candidate) => (
                        <div key={candidate.client_id} className="flex flex-wrap items-center gap-2">
                            <span className="text-xs">
                                {candidate.client_name}
                                {clientPhone(candidate) ? ` · ${formatPhoneNumber(clientPhone(candidate))}` : ""}
                            </span>
                            <button
                                type="button"
                                disabled={busy}
                                onClick={() => linkClient(candidate)}
                                className={`${buttonClass} bg-white text-amber-900 border border-amber-300 hover:bg-amber-100`}
                            >
                                Use
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {showSearch && (
                <div className="mt-2">
                    <div className="flex gap-2">
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(event) => setSearchTerm(event.target.value)}
                            onKeyDown={(event) => {
                                if (event.key === "Enter" && searchTerm.trim()) {
                                    event.preventDefault();
                                    setSearching(true);
                                    searchClients(searchTerm, "client", chain.location_id || null)
                                        .then((result) => setResults(result.clients || []))
                                        .catch((err) => setError(err.message))
                                        .finally(() => setSearching(false));
                                }
                            }}
                            placeholder="Search DaySmart to link a client"
                            className="flex-1 border border-amber-200 rounded-md px-2 py-1 text-xs bg-white"
                        />
                        <button
                            type="button"
                            disabled={searching || !searchTerm.trim()}
                            onClick={() => {
                                setSearching(true);
                                searchClients(searchTerm, "client", chain.location_id || null)
                                    .then((result) => setResults(result.clients || []))
                                    .catch((err) => setError(err.message))
                                    .finally(() => setSearching(false));
                            }}
                            className={`${buttonClass} bg-white text-amber-900 border border-amber-300`}
                        >
                            {searching ? "Searching..." : "Search"}
                        </button>
                    </div>
                    {results.length > 0 && (
                        <div className="mt-1 border border-amber-200 rounded-md bg-white max-h-36 overflow-y-auto">
                            {results.map((client) => (
                                <button
                                    key={`${client.location_id}-${client.client_id}`}
                                    type="button"
                                    disabled={busy}
                                    onClick={() => linkClient(client)}
                                    className="w-full text-left px-2 py-1.5 text-xs hover:bg-amber-50 border-b border-amber-100 last:border-b-0"
                                >
                                    {client.first_name} {client.last_name}
                                    <span className="text-gray-500">
                                        {" "}· {formatPhoneNumber(clientPhone(client))}
                                    </span>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            )}
            {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
        </div>
    );
};

export default ClientLinkNotice;
